import React, { useEffect, useState } from "react"

import { graphql } from "gatsby"
import PropTypes from "prop-types"

import Button from "../components/atoms/button"
import FormField from "../components/atoms/form-field"
import Grid from "../components/atoms/grid"
import Heading from "../components/atoms/heading"
import Icon from "../components/atoms/icon"
import Title from "../components/atoms/title"
import Wrapper from "../components/atoms/wrapper"
import Seo from "../components/head"
import Layout from "../components/layout"
import Alert from "../components/molecules/alert"
import Form from "../components/molecules/form"
import NewsletterBetting from "../components/molecules/newsletter-betting"
import NewsletterFooter from "../components/molecules/newsletter-footer"
import NewsletterGame from "../components/molecules/newsletter-game"
import NewsletterGistPicks from "../components/molecules/newsletter-gist-picks"
import NewsletterIntro from "../components/molecules/newsletter-intro"
import NewsletterQotd from "../components/molecules/newsletter-qotd"
import NewsletterQuestion from "../components/molecules/newsletter-question"
import NewsletterRichText from "../components/molecules/newsletter-rich-text"
import NewsletterSectionTitle from "../components/molecules/newsletter-section-title"
import NewsletterSocial from "../components/molecules/newsletter-social"
import NewsletterStory from "../components/molecules/newsletter-story"
import NewsletterTable from "../components/molecules/newsletter-table"
import NewsletterTopLevel from "../components/molecules/newsletter-top-level"
import NewsletterArchive from "../components/organisms/newsletter-archive"
import isRegion from "../helpers/newsletter-region"
import TimeZone from "../helpers/timezone-offset"
import UrlFunctions from "../helpers/url"

const NewsletterPage = ({ data, pageContext }) => {
  const [city, setCity] = useState("")
  const [segment, setSegment] = useState("")
  const newsletter = data.sanityArchive || data.sanityCampaign

  const updateCity = (e) => {
    setCity(e.target.value)
    const urlParams = new URLSearchParams(window.location.search)
    urlParams.set("city", e.target.value)
    window.location.search = urlParams
  }

  useEffect(() => {
    const urlCity = UrlFunctions.getParams().city
    const urlSegment = UrlFunctions.getParams().segment
    if (urlCity) setCity(urlCity)
    if (urlSegment) setSegment(urlSegment)
  }, [])

  return (
    <Layout>
      <Title color={newsletter.vertical.color}>
        <Grid col={2} tight>
          {data.sanityArchive ? (
            <div>
              <Heading>
                {newsletter.vertical.name}{" "}
                {
                  data.allSanityDictionary.nodes.find(
                    (item) => item.key.current === "newsletter"
                  )?.value
                }
              </Heading>
              <Heading level={5}>
                {new Intl.DateTimeFormat("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "2-digit",
                }).format(new Date(`${newsletter.date}T00:00:00.000`))}
              </Heading>
            </div>
          ) : (
            <div>
              <Heading>{newsletter.subject?.subject_line}</Heading>
              <Heading level={5}>
                {
                  data.allSanityDictionary.nodes.find(
                    (item) => item.key.current === "from"
                  )?.value
                }{" "}
                {newsletter.sender} ({newsletter.sender_email})
              </Heading>
            </div>
          )}
          <Form buttonVariant="none">
            {newsletter.vertical.slug?.current === "daily-sports" &&
            !data.sanityArchive ? (
              <FormField
                label={
                  data.allSanityDictionary.nodes.find(
                    (item) => item.key.current === "city"
                  )?.value
                }
                form="region"
                field="city"
                value={city}
                type="select"
                changeEvent={updateCity}
                options={data.allSanityCity.nodes.map((c) => ({
                  ...c,
                  value: c.abbreviation,
                }))}
                light
              />
            ) : (
              ""
            )}
            <Button url="/newsletters">
              {
                data.allSanityDictionary.nodes.find(
                  (item) => item.key.current === "view_newsletters"
                )?.value
              }
              <Icon icon="arrow_right" />
            </Button>
          </Form>
        </Grid>
      </Title>
      {new Date() > new Date(`${newsletter.date}T07:00:00${TimeZone()}`) ? (
        <div>
          {data.sanityArchive ? (
            <NewsletterArchive newsletter={newsletter.url} />
          ) : (
            <>
              {newsletter.alert_banners?.map((alert) =>
                alert.country?.abbreviation === pageContext.country ||
                !alert.country ? (
                  <Alert content={alert} />
                ) : (
                  ""
                )
              )}
              <Wrapper container newsletter>
                <NewsletterTopLevel
                  section={newsletter}
                  country={pageContext.country}
                  city={city}
                  segment={segment}
                />
                {newsletter.intro ? (
                  <>
                    <NewsletterSectionTitle section={newsletter.intro} />
                    {newsletter.intro.content.map((content) =>
                      isRegion(content, pageContext.country, city, segment) ? (
                        <NewsletterIntro section={content} />
                      ) : (
                        ""
                      )
                    )}
                  </>
                ) : (
                  ""
                )}
                {newsletter.content.map((content) => {
                  if (isRegion(content, pageContext.country, city, segment)) {
                    if (content._type === "campaign_betting") {
                      return <NewsletterBetting section={content} />
                    }
                    if (content._type === "campaign_game") {
                      return <NewsletterGame section={content} />
                    }
                    if (content._type === "campaign_gist_picks") {
                      return (
                        <NewsletterGistPicks
                          section={content}
                          styling={newsletter.styling}
                          country={pageContext.country}
                          city={city}
                          segment={segment}
                        />
                      )
                    }
                    if (content._type === "campaign_intro") {
                      return <NewsletterIntro section={content} />
                    }
                    if (content._type === "campaign_qotd") {
                      return <NewsletterQotd section={content} />
                    }
                    if (content._type === "campaign_question") {
                      return <NewsletterQuestion section={content} />
                    }
                    if (content._type === "campaign_rich_text") {
                      return (
                        <NewsletterRichText
                          styling={newsletter.styling}
                          section={content}
                        />
                      )
                    }
                    if (content._type === "campaign_section_title") {
                      return (
                        <NewsletterSectionTitle
                          styling={newsletter.styling}
                          section={content}
                        />
                      )
                    }
                    if (content._type === "campaign_social") {
                      return (
                        <NewsletterSocial
                          styling={newsletter.styling}
                          section={content}
                        />
                      )
                    }
                    if (content._type === "campaign_article") {
                      return (
                        <>
                          {content.regional ? (
                            <NewsletterSectionTitle
                              section={{
                                color: "white",
                                title: `${
                                  data.allSanityDictionary.nodes.find(
                                    (item) =>
                                      item.key.current === "local_prefix"
                                  )?.value
                                } ${content.region_geographic.name}`,
                              }}
                            />
                          ) : (
                            ""
                          )}
                          <NewsletterStory
                            section={content}
                            country={pageContext.country}
                            city={city}
                            segment={segment}
                            styling={newsletter.styling}
                          />
                        </>
                      )
                    }
                    if (content._type === "campaign_table_content") {
                      return (
                        <NewsletterTable
                          section={content}
                          color={
                            newsletter.special_edition
                              ? content.color ||
                                newsletter.vertical.special_color
                              : content.color || newsletter.vertical.color
                          }
                        />
                      )
                    }
                    return null
                  }
                  return null
                })}
                {newsletter.gist_picks ? (
                  <>
                    <NewsletterSectionTitle section={newsletter.gist_picks} />
                    <NewsletterGistPicks
                      section={newsletter.gist_picks}
                      country={pageContext.country}
                      city={city}
                      segment={segment}
                    />
                  </>
                ) : (
                  ""
                )}
                {newsletter.fun_games.map((content) => {
                  if (isRegion(content, pageContext.country, city)) {
                    if (content._type === "campaign_betting") {
                      return <NewsletterBetting section={content} />
                    }
                    if (content._type === "campaign_game") {
                      return <NewsletterGame section={content} />
                    }
                    if (content._type === "campaign_question") {
                      return <NewsletterQuestion section={content} />
                    }
                    if (content._type === "campaign_section_title") {
                      return <NewsletterSectionTitle section={content} />
                    }
                    return null
                  }
                  return null
                })}
                <NewsletterFooter
                  section={newsletter}
                  country={pageContext.country}
                  city={city}
                  segment={segment}
                />
              </Wrapper>
            </>
          )}
        </div>
      ) : (
        <Wrapper container>
          <Heading level={5} center>
            {
              data.allSanityDictionary.nodes.find(
                (item) => item.key.current === "coming_soon"
              )?.value
            }
          </Heading>
        </Wrapper>
      )}
    </Layout>
  )
}

NewsletterPage.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

export default NewsletterPage

const HeadComponent = ({ data, pageContext, location }) => {
  const newsletter = data.sanityArchive || data.sanityCampaign
  return (
    <Seo
      title={
        data.sanityCampaign?.subject?.subject_line ||
        `${data.sanityArchive?.vertical.name} ${
          data.allSanityDictionary.nodes.find(
            (item) => item.key.current === "newsletter"
          )?.value
        }`
      }
      path={location.pathname}
      prevPage={pageContext.prevPage}
      nextPage={pageContext.prevPage}
      canonical={
        pageContext.latest
          ? `/newsletter/${newsletter.vertical.slug.current}/${pageContext.id}`
          : ""
      }
    />
  )
}

HeadComponent.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
}

export const Head = HeadComponent

export const pageQuery = graphql`
  query newsletterQuery($id: String!, $country: String!) {
    allSanityDictionary {
      nodes {
        key {
          current
        }
        value
      }
    }
    allSanityCity(filter: { country: { abbreviation: { eq: $country } } }) {
      nodes {
        name
        abbreviation
      }
    }
    sanityArchive(_id: { eq: $id }) {
      url
      date
      vertical {
        color
        name
        slug {
          current
        }
      }
    }
    sanityCampaign(_id: { eq: $id }) {
      id: cm_id
      vertical {
        slug {
          current
        }
        color
        special_color
      }
      styling
      date
      alert_banners {
        url
        text
        country {
          abbreviation
        }
        color
      }
      headliner
      issue_number
      logo {
        ...ImageWithPreview
      }
      subject {
        subject_line
      }
      sender
      sender_email
      special_edition
      pixels {
        region_segment
        region_geographic {
          ... on SanityCity {
            abbreviation
            country {
              abbreviation
            }
            _type
          }
          ... on SanityCountry {
            abbreviation
            _type
          }
        }
        region_conference {
          ... on SanityConference {
            name
          }
        }
        pixels {
          code
        }
      }
      presenting_partners {
        url
        partner {
          url
          prefix
          logo {
            ...ImageWithPreview
          }
        }
        country {
          abbreviation
        }
        region_segment
        region_geographic {
          ... on SanityCity {
            abbreviation
            country {
              abbreviation
            }
            _type
          }
          ... on SanityCountry {
            abbreviation
            _type
          }
        }
        region_conference {
          ... on SanityConference {
            name
          }
        }
        contain
      }
      _rawCredits
      intro {
        title
        color
        content {
          title
          _rawContent
          has_cta
          cta_color
          cta_text
          cta_url
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
      }
      gist_picks {
        title
        color
        _rawIntro
        content {
          _rawContent
          title
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
      }
      content {
        ... on SanityCampaignTableContent {
          _type
          color
          table: content {
            rows {
              cells
            }
          }
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignSocial {
          _type
          title
          subtitle
          _rawDescription
          partner {
            name
            prefix
          }
          icon {
            image {
              ...ImageWithPreview
            }
          }
          posts {
            title
            caption
            url
            image {
              ...ImageWithPreview
            }
          }
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignSectionTitle {
          _type
          title
          color
          region_segment
          icon {
            image {
              ...ImageWithPreview
            }
          }
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignRichText {
          _type
          title
          subtitle
          icon {
            image {
              ...ImageWithPreview
            }
          }
          title_centered
          closed
          color
          _rawContent
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignQuestion {
          _key
          _type
          options
          _rawContent
          title
          poll_id
          page {
            slug {
              current
            }
          }
          partner {
            name
            logo {
              ...ImageWithPreview
            }
            url
          }
          partner_url
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignQotd {
          _type
          quote
          _rawAttribution
          image {
            ...ImageWithPreview
          }
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignIntro {
          _type
          title
          _rawContent
          has_cta
          cta_color
          cta_text
          cta_url
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignGistPicks {
          _type
          _rawIntro
          numbered
          content {
            _rawContent
            title
            url
            caption
            image {
              ...ImageWithPreview
            }
            region_segment
            region_geographic {
              ... on SanityCity {
                abbreviation
                country {
                  abbreviation
                }
                _type
              }
              ... on SanityCountry {
                abbreviation
                _type
              }
            }
            region_conference {
              ... on SanityConference {
                name
              }
            }
          }
        }
        ... on SanityCampaignGame {
          _type
          _rawDescription
          image {
            ...ImageWithPreview
          }
          game {
            ... on SanityQuiz {
              _type
              slug {
                current
              }
            }
            ... on SanityPuzzle {
              _type
              slug {
                current
              }
            }
          }
          title
          partner {
            name
            logo {
              ...ImageWithPreview
            }
            url
          }
          partner_url
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignCtas {
          _key
          _type
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignBetting {
          _type
          partner {
            name
            logo {
              ...ImageWithPreview
            }
            url
          }
          partner_url
          title
          _rawContent
          poll_id
          page {
            slug {
              current
            }
          }
          teams {
            name
            odds
            logo {
              ...ImageWithPreview
            }
          }
          show_cta
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignArticle {
          _type
          regional
          color
          article {
            title
            subject
            is_ss_se
            slug {
              current
            }
            publishing
            web_article_type
            partner {
              name
              logo {
                ...ImageWithPreview
              }
              url
              prefix
            }
            partner_url
            campaign_partner_type
            featured_image {
              caption
              alt
              url
              image {
                ...ImageWithPreview
              }
            }
            _rawContent
            additional_article_sections {
              partner {
                name
                logo {
                  ...ImageWithPreview
                }
                url
                prefix
              }
              partner_url
              campaign_partner_type
              featured_image {
                image {
                  ...ImageWithPreview
                }
              }
              _rawContent
              visible
              subject
              title
              region {
                ... on SanityCity {
                  abbreviation
                  country {
                    abbreviation
                  }
                  _type
                }
                ... on SanityCountry {
                  abbreviation
                  _type
                }
              }
            }
          }
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              name
              image {
                ...ImageWithPreview
              }
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              name
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
      }
      fun_games {
        ... on SanityCampaignSectionTitle {
          _type
          title
          color
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignQuestion {
          _key
          _type
          options
          _rawContent
          title
          poll_id
          page {
            slug {
              current
            }
          }
          partner {
            name
            logo {
              ...ImageWithPreview
            }
            url
          }
          partner_url
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignGame {
          _type
          _rawDescription
          image {
            ...ImageWithPreview
          }
          game {
            ... on SanityQuiz {
              _type
              slug {
                current
              }
            }
            ... on SanityPuzzle {
              _type
              slug {
                current
              }
            }
          }
          title
          partner {
            name
            logo {
              ...ImageWithPreview
            }
            url
          }
          partner_url
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
        ... on SanityCampaignBetting {
          _type
          partner {
            name
            logo {
              ...ImageWithPreview
            }
            url
          }
          partner_url
          title
          _rawContent
          poll_id
          page {
            slug {
              current
            }
          }
          teams {
            name
            odds
            logo {
              ...ImageWithPreview
            }
          }
          show_cta
          region_segment
          region_geographic {
            ... on SanityCity {
              abbreviation
              country {
                abbreviation
              }
              _type
            }
            ... on SanityCountry {
              abbreviation
              _type
            }
          }
          region_conference {
            ... on SanityConference {
              name
            }
          }
        }
      }
    }
  }
`
